import { inject, Injectable } from "@angular/core";
import { DataPluginStoreService } from "../../services/store/data-plugin/data-plugin.store.service";
import { UserStoreService } from "../../services/store/user/user.store.service";
import { InstitutionView } from "../../models/view/institution/institution.view";
import { GLOSS_VAULT } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { isPluginInstitution } from "@bitwarden/web-vault/app/shared/utils/helper.institution/plugin-institution.helper";
import { isSelectableCatalogInstitution } from "@bitwarden/web-vault/app/shared/utils/helper.institution/selectable-institution.helper";

@Injectable({
  providedIn: "root",
})
export class ManualInstitution {
  private dataPluginStore = inject(DataPluginStoreService);
  private userStore = inject(UserStoreService);
  private confirmationService = inject(ConfirmationDialogService);

  isValid(institutionView: InstitutionView): boolean {
    if (!this.isValidName(institutionView.name)) {
      return false;
    }

    if (this.isInstoInNocoDb(institutionView.name)) {
      return false;
    }

    return !this.isDuplicateName(institutionView);
  }

  private isValidName(institutionName: string): boolean {
    const name = institutionName.trim();

    if (!name) {
      return false;
    }

    // Allow letters, numbers, and hyphen only. No numbers-only!!
    const regex = /^(?=.*[A-Za-z])[A-Za-z0-9 -]+$/;

    // Reserved for the system GLOSS credits institution.
    const isReserved = name.toLowerCase() === GLOSS_VAULT.toLowerCase();

    const isValid = regex.test(name) && !isReserved;
    if (!isValid) {
      this.confirmationService.notify("error", "error", "invalidInstitutionName");
    }

    return isValid;
  }

  private isInstoInNocoDb(institutionName: string): boolean {
    const normalizedInput = institutionName.trim().toLowerCase();

    // Only institutions the add-account dropdown offers: for the others (plugin, GLOSS Vault)
    // "choose the existing option" would point at nothing.
    // The catalog is null until it loads (and after clear() or a failed fetch). Validate against
    // nothing rather than block: adding a manual institution is the fallback when the fetch failed,
    // and a manual institution sharing a catalog name is already handled (institutionNames dedupe).
    const isInDb = (this.dataPluginStore.theInstitutionList.theInstitutionViews() ?? []).some(
      (institution) =>
        isSelectableCatalogInstitution(institution) &&
        institution.name.trim().toLowerCase() === normalizedInput,
    );

    if (isInDb) {
      this.confirmationService.notify("error", "error", "thereIsAnInstitutionOptionForYou");
    }

    return isInDb;
  }

  /** If user has that institution on that country*/
  private isDuplicateName(institutionView: InstitutionView): boolean {
    const normalizedInput = institutionView.name.trim().toLowerCase();

    const isDuplicate = this.userStore.institutions.institutionViews().some(
      (institution) =>
        // Connecting a plugin saves its institution (e.g. Opal, AU) here too
        // (resolvePluginInstitution), which isn't one the user created.
        !isPluginInstitution(institution) &&
        institution.name.trim().toLowerCase() === normalizedInput &&
        institution.swift.countryCode === institutionView.swift.countryCode,
    );

    if (isDuplicate) {
      this.confirmationService.notify("error", "error", "youHaveCreatedThisInstitutionBefore");
    }

    return isDuplicate;
  }
}
